import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CommentsList } from './CommentsList';
import { CommentComposer } from './CommentComposer';
import { MessageSquare } from 'lucide-react';
import { useComments } from '@/hooks/useKBData';

interface CommentsPanelProps {
  articleSlug: string;
  isOpen: boolean;
  onClose: () => void;
  mode?: 'drawer' | 'panel';
}

export function CommentsPanel({ articleSlug, isOpen, onClose, mode = 'drawer' }: CommentsPanelProps) {
  const { data: commentsData, isLoading } = useComments(articleSlug);
  const comments = commentsData?.comments || [];

  if (mode === 'panel') {
    // Desktop side panel
    if (!isOpen) return null;

    return (
      <div className="fixed right-0 top-0 h-full w-96 bg-background border-l border-border shadow-lg z-50 flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Comments ({comments.length})
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded transition-colors"
              aria-label="Close comments panel"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <CommentComposer articleSlug={articleSlug} />

          <div className="flex-1 overflow-y-auto">
            <CommentsList
              comments={comments}
              articleSlug={articleSlug}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    );
  }

  // Mobile drawer
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[85vh] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Comments ({comments.length})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-hidden mt-4">
          <CommentComposer articleSlug={articleSlug} />

          <div className="flex-1 overflow-y-auto">
            <CommentsList
              comments={comments}
              articleSlug={articleSlug}
              isLoading={isLoading}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}